/**
 * AutoMLDashboard — Launch sweeps via wizard, view experiments, compare runs
 * Phase 4.8-B — WQ-198 (initial)
 * Phase 4.10 β2 — WQ-237: replaced inline 3-field form with AutoMLWizard
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import {
  getSweepStatus, listExperiments,
  type SweepStatus, type Experiment,
} from '../../services/automlService';
import AutoMLWizard from './AutoMLWizard';

const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e', running: '#3b82f6', pending: '#eab308',
  failed: '#dc2626', cancelled: '#6b7280',
};

const AutoMLDashboard: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [sweepStatus, setSweepStatus] = useState<SweepStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchExperiments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listExperiments(authFetch);
      setExperiments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load experiments');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchExperiments();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [authLoading]);

  const handleSweepLaunched = (sweepId: string) => {
    // Wizard already invoked startSweep; refresh experiments and start polling.
    setError(null);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const updated = await getSweepStatus(authFetch, sweepId);
        setSweepStatus(updated);
        if (updated.status === 'completed' || updated.status === 'failed' || updated.status === 'cancelled') {
          if (pollRef.current) clearInterval(pollRef.current);
          fetchExperiments();
        }
      } catch { /* polling error — ignore */ }
    }, 5000);
    fetchExperiments();
  };

  if (loading && experiments.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading AutoML...</div>;
  }

  return (
    <div>
      {/* Header actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <button
          data-testid="automl-launch-button"
          onClick={() => setWizardOpen(true)}
          style={{
            fontSize: 13, padding: '6px 16px', borderRadius: 6,
            border: 'none', fontWeight: 600, cursor: 'pointer',
            background: 'var(--accent-primary)', color: '#fff',
          }}
        >
          Launch AutoML
        </button>
        <button onClick={fetchExperiments} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
          fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {/* Active sweep status */}
      {sweepStatus && sweepStatus.status === 'running' && (
        <div style={{
          padding: 12, borderRadius: 8, border: '1px solid rgba(59,130,246,0.3)',
          background: 'rgba(59,130,246,0.05)', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>Sweep Running</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {sweepStatus.trials_completed}/{sweepStatus.trials_total} trials
            </span>
          </div>
          <div style={{ marginTop: 8, height: 6, background: 'var(--bg-secondary)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${sweepStatus.progress * 100}%`, background: '#3b82f6', borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
          {sweepStatus.best_algorithm && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              Best so far: {sweepStatus.best_algorithm} ({sweepStatus.best_metric?.toFixed(4)})
            </div>
          )}
        </div>
      )}

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Experiments list */}
      {experiments.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No experiments yet. Launch a sweep to get started.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Problem Type</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Runs</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Best Metric</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {experiments.map(exp => (
                <tr key={exp.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{exp.name}</td>
                  <td style={{ padding: '8px 12px' }}>{exp.problem_type}</td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${STATUS_COLORS[exp.status] || '#888'}20`,
                      color: STATUS_COLORS[exp.status] || '#888',
                    }}>{exp.status}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>{exp.run_count}</td>
                  <td style={{ textAlign: 'right', padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    {exp.best_metric !== null ? exp.best_metric.toFixed(4) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(exp.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AutoMLWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onLaunched={handleSweepLaunched}
      />
    </div>
  );
};

export default AutoMLDashboard;
